import { IconCheck, IconLock, IconUser } from "@tabler/icons-react";
import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError, type Traveller, type TravellerInput, type TravellersView } from "../api";
import { formatDate } from "../format";
import { PAYMENT_ERROR_KEYS, TRAVELLER_STATUS_KEYS, translate } from "../i18n";
import { useSession } from "../session";
import { Card, Empty, ErrorNote, Field, SectionTitle, Spinner } from "./ui";

/** Common nationalities for the brands' customers; anything else by its 2-letter code. */
const COUNTRIES = ["PT", "ES", "FR", "DE", "GB", "US", "CA", "BR", "IT", "NL", "BE", "CH", "AT", "IE", "LU", "SE", "DK", "NO", "FI", "PL", "AU", "AO", "MZ", "CV"];
const OTHER = "__other";

function countryName(code: string, locale: string) {
  try {
    return new Intl.DisplayNames([locale], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** Traveller details per slot (docs/16 decision 12): the holder fills every slot, a companion their own; validated rows lock. */
export function TravellersCard({ tripId, onChanged }: { tripId: string; onChanged?: (view: TravellersView) => void }) {
  const { lang, t } = useSession();
  const [view, setView] = useState<TravellersView | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [editing, setEditing] = useState<number | null>(null);
  const [savedSlot, setSavedSlot] = useState<number | null>(null);

  useEffect(() => {
    setError(null);
    api
      .travellers(tripId)
      .then((res) => {
        setView(res);
        onChanged?.(res);
      })
      .catch(() => setError(t("somethingWrong")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId, tick]);

  const onSaved = (row: Traveller) => {
    setView((prev) => {
      if (!prev) return prev;
      const next = { ...prev, travellers: prev.travellers.map((x) => (x.slot === row.slot ? row : x)) };
      onChanged?.(next);
      return next;
    });
    setEditing(null);
    setSavedSlot(row.slot);
  };

  return (
    <>
      <SectionTitle>{t("travellerDetails")}</SectionTitle>
      {view === undefined && !error ? (
        <Card>
          <Spinner />
        </Card>
      ) : error ? (
        <ErrorNote onRetry={() => setTick((n) => n + 1)}>{error}</ErrorNote>
      ) : view && view.travellers.length === 0 ? (
        <Empty title={t("noPlanYet")} />
      ) : (
        view && (
          <Card className="travellers-card">
            {view.dueOn && <p className="small travellers-due">{t("travellersDueBy", { date: formatDate(view.dueOn, lang) })}</p>}
            <ul className="list">
              {view.travellers.map((tr) => {
                const editable = view.editableSlots.includes(tr.slot) && tr.status !== "validated";
                const statusKey = TRAVELLER_STATUS_KEYS[tr.status];
                return (
                  <li key={tr.id} className="traveller">
                    <div className="list-row">
                      <span className="list-icon">{tr.status === "validated" ? <IconCheck size={20} stroke={1.75} /> : <IconUser size={20} stroke={1.5} />}</span>
                      <span className="list-text">
                        <strong>{tr.givenNames || tr.surname ? `${tr.givenNames ?? ""} ${tr.surname ?? ""}`.trim() : t("travellerN", { n: tr.slot })}</strong>
                        <span className="muted small">
                          {t("travellerN", { n: tr.slot })}
                          {tr.documentNumber ? ` · ${tr.documentType === "passport" ? t("passport") : t("idCard")} ${tr.documentNumber}` : ""}
                        </span>
                        {tr.status === "rejected" && tr.rejectedReason && <span className="small rejected">{t("rejectedReason", { reason: tr.rejectedReason })}</span>}
                        {savedSlot === tr.slot && <span className="small saved">{t("travellerSaved")}</span>}
                      </span>
                      <span className={`chip chip-tr-${tr.status}`}>{statusKey ? t(statusKey) : tr.status}</span>
                      {tr.status === "validated" ? (
                        <IconLock size={16} className="muted" aria-hidden="true" />
                      ) : (
                        editable && (
                          <button type="button" className="button button-small" onClick={() => setEditing(editing === tr.slot ? null : tr.slot)}>
                            {tr.status === "missing" ? t("enterDetails") : t("edit")}
                          </button>
                        )
                      )}
                    </div>
                    {editing === tr.slot && <TravellerForm tripId={tripId} traveller={tr} onSaved={onSaved} onCancel={() => setEditing(null)} />}
                  </li>
                );
              })}
            </ul>
            <p className="muted small">{t("docsNote")}</p>
          </Card>
        )
      )}
    </>
  );
}

function TravellerForm({ tripId, traveller, onSaved, onCancel }: { tripId: string; traveller: Traveller; onSaved: (row: Traveller) => void; onCancel: () => void }) {
  const { lang, t } = useSession();
  const locale = lang;
  const known = traveller.nationality && COUNTRIES.includes(traveller.nationality);
  const [givenNames, setGivenNames] = useState(traveller.givenNames ?? "");
  const [surname, setSurname] = useState(traveller.surname ?? "");
  const [dateOfBirth, setDateOfBirth] = useState(traveller.dateOfBirth ?? "");
  const [country, setCountry] = useState(known ? traveller.nationality! : traveller.nationality ? OTHER : "PT");
  const [otherCountry, setOtherCountry] = useState(known ? "" : (traveller.nationality ?? ""));
  const [documentType, setDocumentType] = useState<"passport" | "id_card">(traveller.documentType === "id_card" ? "id_card" : "passport");
  const [documentNumber, setDocumentNumber] = useState("");
  const [documentExpiry, setDocumentExpiry] = useState(traveller.documentExpiry ?? "");
  const [gender, setGender] = useState<"" | "F" | "M" | "X">(traveller.gender === "F" || traveller.gender === "M" || traveller.gender === "X" ? traveller.gender : "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const nationality = (country === OTHER ? otherCountry : country).trim().toUpperCase();
  const valid = givenNames.trim() && surname.trim() && dateOfBirth && /^[A-Z]{2}$/.test(nationality) && documentNumber.trim().length >= 3 && documentExpiry;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    const body: TravellerInput = { givenNames: givenNames.trim(), surname: surname.trim(), dateOfBirth, nationality, documentType, documentNumber: documentNumber.trim(), documentExpiry, ...(gender ? { gender } : {}) };
    try {
      onSaved((await api.putTraveller(tripId, traveller.slot, body)).traveller);
    } catch (err) {
      if (err instanceof ApiError && PAYMENT_ERROR_KEYS[err.code]) setError(translate(lang, PAYMENT_ERROR_KEYS[err.code]!));
      else setError(t("somethingWrong"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="form traveller-form" onSubmit={(e) => void submit(e)}>
      <div className="form-grid">
        <Field label={t("givenNames")}>
          <input value={givenNames} onChange={(e) => setGivenNames(e.target.value)} autoComplete="given-name" required maxLength={100} />
        </Field>
        <Field label={t("surname")}>
          <input value={surname} onChange={(e) => setSurname(e.target.value)} autoComplete="family-name" required maxLength={100} />
        </Field>
        <Field label={t("dateOfBirth")}>
          <input type="date" value={dateOfBirth} onChange={(e) => setDateOfBirth(e.target.value)} required max={new Date().toISOString().slice(0, 10)} />
        </Field>
        <Field label={t("nationality")}>
          <select value={country} onChange={(e) => setCountry(e.target.value)}>
            {COUNTRIES.map((c) => (
              <option key={c} value={c}>
                {countryName(c, locale)}
              </option>
            ))}
            <option value={OTHER}>{t("otherCountry")}</option>
          </select>
        </Field>
        {country === OTHER && (
          <Field label={t("countryCode")}>
            <input value={otherCountry} onChange={(e) => setOtherCountry(e.target.value.toUpperCase())} maxLength={2} pattern="[A-Za-z]{2}" placeholder="XX" required />
          </Field>
        )}
        <Field label={t("documentType")}>
          <select value={documentType} onChange={(e) => setDocumentType(e.target.value === "id_card" ? "id_card" : "passport")}>
            <option value="passport">{t("passport")}</option>
            <option value="id_card">{t("idCard")}</option>
          </select>
        </Field>
        <Field label={t("documentNumber")} hint={traveller.documentNumber ?? undefined}>
          <input value={documentNumber} onChange={(e) => setDocumentNumber(e.target.value)} required minLength={3} maxLength={30} autoComplete="off" />
        </Field>
        <Field label={t("documentExpiry")}>
          <input type="date" value={documentExpiry} onChange={(e) => setDocumentExpiry(e.target.value)} required min={new Date().toISOString().slice(0, 10)} />
        </Field>
        <Field label={t("gender")}>
          <select value={gender} onChange={(e) => setGender(e.target.value as "" | "F" | "M" | "X")}>
            <option value="">—</option>
            <option value="F">{t("genderF")}</option>
            <option value="M">{t("genderM")}</option>
            <option value="X">{t("genderX")}</option>
          </select>
        </Field>
      </div>
      {error && <ErrorNote>{error}</ErrorNote>}
      <div className="row-buttons">
        <button type="submit" className="button button-primary" disabled={busy || !valid}>
          {busy ? t("saving") : t("save")}
        </button>
        <button type="button" className="link-button" onClick={onCancel}>
          {t("cancel")}
        </button>
      </div>
    </form>
  );
}
